import React, { useEffect, useRef, useState } from 'react';
import { ChevronUp, Lock, LockOpen, Minus, Paperclip, Send, Trash2, TriangleAlert, X } from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import { Toggle } from './ui.jsx';

const HEADER = '[KryptaMail post-quantum encrypted message]\n\n';

/**
 * A chip input with contact autocomplete, used for To / Cc / Bcc. Suggestions are
 * drawn from the account's contacts; keyboard-navigable (↑/↓/Enter), click to add.
 */
function RecipientField({ label, value, onChange, contacts, keys }) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const q = input.trim().toLowerCase();
  const suggestions = q
    ? contacts.filter((c) => c.email && !value.includes(c.email) && (`${c.name || ''} ${c.email}`.toLowerCase().includes(q))).slice(0, 6)
    : [];
  const add = (addr) => { const v = (addr || '').trim().replace(/[,;]$/, ''); if (v && !value.includes(v)) onChange([...value, v]); setInput(''); setOpen(false); setHi(0); };
  const onKey = (e) => {
    if (open && suggestions.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => (h + 1) % suggestions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => (h - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); add(suggestions[hi].email); return; }
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && input.trim()) { e.preventDefault(); add(input); }
    else if (e.key === 'Backspace' && !input && value.length) onChange(value.slice(0, -1));
  };
  return (
    <div className="km-compose-row" style={{ flexWrap: 'wrap', position: 'relative', minHeight: 44 }}>
      <span className="km-faint" style={{ fontSize: 13, width: 42 }}>{label}</span>
      {value.map((c) => { const k = keys[c]; return <span key={c} className={`km-chip ${k === false ? 'nokey' : ''}`} title={k === false ? 'No PQC key' : k ? 'PQC key found in directory' : 'Checking key…'}>{k === false ? <LockOpen size={12} color="var(--km-warn)" /> : <Lock size={12} color={k ? 'var(--km-ok)' : 'var(--km-faint)'} />}{c}<button type="button" onClick={() => onChange(value.filter((x) => x !== c))}><X size={12} /></button></span>; })}
      <input value={input} onChange={(e) => { const v = e.target.value; if (/[,;\s]$/.test(v)) add(v); else { setInput(v); setOpen(true); setHi(0); } }} onKeyDown={onKey} onBlur={() => { setTimeout(() => setOpen(false), 120); if (input.trim()) add(input); }} placeholder={value.length ? '' : 'name@company.com'} style={{ flex: 1, minWidth: 140, background: 'transparent', border: 'none', outline: 'none', fontSize: 14, padding: '4px 0' }} />
      {open && suggestions.length > 0 && (
        <div className="km-ac">
          {suggestions.map((c, i) => (
            <button key={c.id || c.email} type="button" className={`km-ac-item ${i === hi ? 'hi' : ''}`} onMouseDown={(e) => { e.preventDefault(); add(c.email); }} onMouseEnter={() => setHi(i)}>
              <span className="km-ac-name">{c.name || c.email}</span><span className="km-ac-email">{c.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Gmail-style docked composer: lives bottom-right, persists across app navigation
 * (rendered by Shell), minimizes to a title bar, and never closes on an outside
 * click - only on explicit discard/send. Encrypts per-recipient (plus a copy to
 * the sender) when all/any recipients have PQC keys; auto-adds new recipients to
 * contacts on send.
 */
export function Compose({ session, initial = {}, contacts = [], dark = false, flash, minimized, onMinimize, onRestore, onClose, onSent }) {
  const { jmap, crypto, email: me, apis } = session;
  const [to, setTo] = useState(initial.to || []);
  const [cc, setCc] = useState(initial.cc || []);
  const [bcc, setBcc] = useState(initial.bcc || []);
  const [showCc, setShowCc] = useState(((initial.cc || []).length + (initial.bcc || []).length) > 0);
  const [subject, setSubject] = useState(initial.subject || '');
  const [body, setBody] = useState(initial.body || '');
  const [encrypt, setEncrypt] = useState(session.prefs?.encryptDefault ?? true);
  const [keys, setKeys] = useState({});
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [draftId, setDraftId] = useState(initial.draftId || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const fileRef = useRef();
  const dragDepth = useRef(0);

  const onDrop = (e) => { e.preventDefault(); dragDepth.current = 0; setDragOver(false); const dropped = [...(e.dataTransfer?.files || [])]; if (dropped.length) setFiles((f) => [...f, ...dropped]); };

  const hasContent = () => all.length || subject.trim() || body.trim() || files.length;
  // Save (or replace) this message in the Drafts folder. Returns the draft id.
  const saveDraft = async ({ silent } = {}) => {
    if (!hasContent()) return null;
    if (!silent) setSavingDraft(true);
    try { const id = await jmap.saveDraft({ draftId, from: me, to, cc, bcc, subject, textBody: body, attachments: files }); if (id) setDraftId(id); return id; }
    catch (e) { if (!silent) setStatus('Could not save draft: ' + e.message); return null; }
    finally { if (!silent) setSavingDraft(false); }
  };
  // X keeps the draft (auto-save on close); Discard (trash) throws it away.
  const closeSaving = async () => { if (hasContent()) { await saveDraft({ silent: true }); flash?.('Draft saved'); } onClose(); };
  const discard = async () => { if (draftId) jmap.deleteEmail(draftId).catch(() => {}); onClose(); };

  const all = [...to, ...cc, ...bcc];
  useEffect(() => { all.forEach((e) => { if (keys[e] === undefined) crypto.lookupRecipient(e).then((r) => setKeys((k) => ({ ...k, [e]: !!r?.kemPublicKey }))); }); }, [to, cc, bcc]); // eslint-disable-line
  const noKey = all.filter((c) => keys[c] === false);
  const withKey = all.length - noKey.length;

  const autoAdd = (emails) => {
    const have = new Set((contacts || []).map((c) => (c.email || '').toLowerCase()));
    for (const e of emails) { const a = e.trim(); const lk = a.toLowerCase(); if (a.includes('@') && !have.has(lk)) { have.add(lk); apis.contacts.create({ name: a.split('@')[0], email: a, title: '', company: '', phone: '', group: 'external', fav: false }).catch(() => {}); } }
  };

  const send = async () => {
    if (!all.length) { setStatus('Add at least one recipient'); return; }
    setBusy(true); setStatus(null);
    try {
      let textBody = body, subj = subject, sealed = false;
      if (encrypt && session.identity?.sig) {
        // Encryption is ALL-OR-NOTHING: JMAP sends one body to every recipient, so
        // we only encrypt when EVERY recipient has a published key. Otherwise a
        // keyless/external recipient (e.g. a gmail address) would receive
        // unreadable ciphertext - so we send plaintext (TLS-only) instead.
        const recs = {};
        for (const r of all) recs[r] = await crypto.lookupRecipient(r);
        const allKeyed = all.every((r) => recs[r]?.kemPublicKey);
        if (allKeyed) {
          // One envelope per recipient PLUS the sender (so the Sent copy is
          // readable). Each envelope carries its `to`; the reader opens its own.
          const seen = new Set();
          const targets = [...all, me].filter((r) => { const k = r.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
          const envelopes = [];
          for (const r of targets) { const rec = recs[r] || await crypto.lookupRecipient(r); if (rec?.kemPublicKey) envelopes.push((await crypto.seal({ from: me, to: r, plaintext: body, recipientKemPublicKey: rec.kemPublicKey, senderSigPrivateKey: session.identity.sig.privateKey })).envelope); }
          if (envelopes.length) { textBody = HEADER + JSON.stringify(envelopes); subj = subject || '[KryptaMail encrypted]'; sealed = true; }
        }
      }
      await jmap.send({ from: me, to, cc, bcc, subject: subj, textBody, attachments: files });
      if (draftId) jmap.deleteEmail(draftId).catch(() => {}); // sent -> remove the draft copy
      autoAdd(all);
      onSent(sealed);
    } catch (e) { setStatus(e.message); setBusy(false); }
  };

  if (minimized) {
    return (
      <div className="km-compose-dock minimized">
        <div className="km-compose-bar" onClick={onRestore}>
          <span className="km-ellipsis">{subject || (to[0] ? `To ${to[0]}` : 'New message')}</span>
          <span style={{ display: 'flex', gap: 6 }}><ChevronUp size={15} /><X size={15} onClick={(e) => { e.stopPropagation(); closeSaving(); }} /></span>
        </div>
      </div>
    );
  }

  return (
    <div className="km-compose-dock">
      <div className="km-compose-win" style={{ position: 'relative' }}
        onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragOver(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => { e.preventDefault(); dragDepth.current -= 1; if (dragDepth.current <= 0) setDragOver(false); }}
        onDrop={onDrop}>
        {dragOver && <div className="km-dropzone"><Paperclip size={22} /><span>Drop files to attach</span></div>}
        <div className="km-compose-head">
          <span className="km-ellipsis">{initial.title || 'New message'}{savingDraft ? ' · saving…' : ''}</span>
          <span style={{ display: 'flex', gap: 2 }}>
            <button className="km-icon-btn sm" title="Minimize" onClick={onMinimize}><Minus size={15} /></button>
            <button className="km-icon-btn sm" title="Discard draft" onClick={discard}><Trash2 size={14} /></button>
            <button className="km-icon-btn sm" title="Save draft & close" onClick={closeSaving}><X size={15} /></button>
          </span>
        </div>
        <div style={{ position: 'relative' }}>
          <RecipientField label="To" value={to} onChange={setTo} contacts={contacts} keys={keys} />
          {!showCc && <button type="button" className="km-btn-ghost" style={{ position: 'absolute', right: 10, top: 8, padding: '2px 4px', fontSize: 12.5 }} onClick={() => setShowCc(true)}>Cc / Bcc</button>}
        </div>
        {showCc && <RecipientField label="Cc" value={cc} onChange={setCc} contacts={contacts} keys={keys} />}
        {showCc && <RecipientField label="Bcc" value={bcc} onChange={setBcc} contacts={contacts} keys={keys} />}
        <div className="km-compose-row"><span className="km-faint" style={{ fontSize: 13, width: 42 }}>Subj</span><input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 14, padding: '10px 0' }} /></div>
        <div data-color-mode={dark ? 'dark' : 'light'} className="km-compose-editor" style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--km-panel)' }}>
          <MDEditor value={body} onChange={(v) => setBody(v || '')} preview="edit" height="100%" visibleDragbar={false} textareaProps={{ placeholder: 'Write your message - **bold**, _italic_, # heading, - list, [link](url), `code`…' }} />
        </div>
        {files.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 16px 10px' }}>{files.map((f, i) => <span key={i} className="km-chip" style={{ background: 'var(--km-bg)' }}><Paperclip size={13} color="var(--km-muted)" />{f.name}<button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={12} /></button></span>)}</div>}
        {encrypt && noKey.length > 0 && <div className="km-keynote"><TriangleAlert size={15} color="var(--km-warn)" style={{ marginTop: 2 }} /><span>{noKey.join(', ')} {noKey.length > 1 ? 'have' : 'has'} no published PQC key yet, so this message is sent with transport (TLS) encryption only - not end-to-end. A KryptaMail user gets a key automatically the first time they sign in.</span></div>}
        {status && <div className="km-keynote" style={{ borderColor: 'var(--km-bad)', color: 'var(--km-bad)' }}>{status}</div>}
        <div className="km-compose-foot">
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--km-text)', fontWeight: 500, fontSize: 13.5 }}><Toggle on={encrypt} small onChange={() => setEncrypt((e) => !e)} /><span style={{ cursor: 'pointer' }} onClick={() => setEncrypt((e) => !e)}>Encrypt E2E</span></span>
          <span className="km-faint" style={{ fontSize: 12 }}>{!encrypt ? 'TLS only' : all.length === 0 ? '' : `${withKey}/${all.length} keyed`}</span>
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => setFiles([...files, ...e.target.files])} />
          <button type="button" className="km-icon-btn" title="Attach file" style={{ marginLeft: 'auto' }} onClick={() => fileRef.current.click()}><Paperclip size={16} /></button>
          <button type="button" className="km-btn" disabled={busy} onClick={send}>{encrypt ? <Lock size={14} /> : <Send size={14} />}{busy ? 'Sending…' : 'Send'}</button>
        </div>
      </div>
    </div>
  );
}
